import {
  Button,
  Checkbox,
  Classes,
  Dialog,
  DialogBody,
  DialogFooter,
  Tooltip,
} from '@blueprintjs/core';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import classes from './ColumnsDialog.module.css';
import { ColumnID, column } from './config';

export function ColumnsDialog({
  available,
  isOpen,
  columns: initialColumns,
  onClose,
  setColumns: onSave,
}: {
  available: ColumnID[];
  isOpen: boolean;
  columns: ColumnID[];
  onClose(): void;
  setColumns: Dispatch<SetStateAction<ColumnID[]>>;
}) {
  const [include, setInclude] = useState(() => new Set(initialColumns));
  const [order, setOrder] = useState(() => getOrder(initialColumns, available));

  useEffect(() => {
    setInclude(new Set(initialColumns));
    setOrder(getOrder(initialColumns, available));
  }, [initialColumns, available]);

  function close() {
    setInclude(new Set(initialColumns));
    setOrder(getOrder(initialColumns, available));
    onClose();
  }

  function handleDone() {
    const values = order.filter((column) => include.has(column));
    onSave(values);
    close();
  }

  return (
    <Dialog
      icon="eye-open"
      title="Modify columns"
      isOpen={isOpen}
      onClose={close}
    >
      <DialogBody>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>
                <Checkbox
                  checked={include.size === available.length}
                  indeterminate={
                    include.size > 0 && include.size < available.length
                  }
                  onChange={() =>
                    setInclude((prev) =>
                      prev.size === available.length
                        ? new Set<ColumnID>(
                            available.filter((id) => column(id)?.mandatory),
                          )
                        : new Set(available),
                    )
                  }
                >
                  Include all
                </Checkbox>
              </th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {order.map((id, i) => {
              const config = column(id);
              if (!config) {
                return null;
              }

              const label = config.label;
              const checked = include.has(id);
              const disabled = config.mandatory && checked;
              return (
                <tr key={id}>
                  <td>
                    <Checkbox
                      disabled={disabled}
                      checked={include.has(id)}
                      onClick={() =>
                        setInclude((prev) => {
                          const next = new Set(prev);
                          if (next.has(id)) next.delete(id);
                          else next.add(id);
                          return next;
                        })
                      }
                    >
                      {label}
                    </Checkbox>
                  </td>
                  <td>
                    <Tooltip content={<span>Move up</span>}>
                      <Button
                        minimal
                        icon="chevron-up"
                        disabled={i === 0}
                        onClick={() =>
                          setOrder((prev) => [
                            ...prev.slice(0, i - 1),
                            prev[i],
                            prev[i - 1],
                            ...prev.slice(i + 1),
                          ])
                        }
                      />
                    </Tooltip>
                  </td>
                  <td>
                    <Tooltip content={<span>Move down</span>}>
                      <Button
                        minimal
                        icon="chevron-down"
                        disabled={i === available.length - 1}
                        onClick={() =>
                          setOrder((prev) => [
                            ...prev.slice(0, i),
                            prev[i + 1],
                            prev[i],
                            ...prev.slice(i + 2),
                          ])
                        }
                      />
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </DialogBody>
      <DialogFooter>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button
            icon="tick"
            intent="primary"
            text="Done"
            onClick={handleDone}
          />
          <Button icon="cross" intent="none" text="Cancel" onClick={close} />
        </div>
      </DialogFooter>
    </Dialog>
  );
}

function getOrder(columns: ColumnID[], available: ColumnID[]): ColumnID[] {
  const include = new Set(columns);

  return [
    ...columns
      .filter((id) => column(id))
      .filter((column) => include.has(column)),
    ...available.filter((column) => !include.has(column)),
  ];
}
